'use client';

import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { LeadCaptureForm } from '@/components/calculator/LeadCaptureForm';

type StepId = 'inventory' | 'assessment' | 'monitoring' | 'strategy';

type SingleSelectOption = {
    value: string;
    label: string;
    helper?: string;
    score: number;
};

type MultiSelectOption = {
    value: string;
    label: string;
    score: number;
};

type Question =
    | {
          id: keyof Answers;
          step: StepId;
          prompt: string;
          type: 'single';
          options: SingleSelectOption[];
      }
    | {
          id: keyof Answers;
          step: StepId;
          prompt: string;
          type: 'multi';
          options: MultiSelectOption[];
      };

type Answers = {
    vendorCount?: string;
    dataSensitivity?: string;
    assessmentMethod?: string;
    evidenceReview?: string;
    proactiveAlerts?: string;
    incidentPlaybook?: string;
    complianceFocus: string[];
    implementationTimeline?: string;
};

type PillarResults = Record<StepId, number>;

export type LeadCapturePayload = {
    name: string;
    email: string;
    company: string;
    role: string;
    answers: Answers;
    pillarScores: PillarResults;
    maturityScore: number;
    maturityTier: string;
    sle: number;
    aro: number;
    ale: number;
};

const weights: Record<StepId, number> = {
    inventory: 0.25,
    assessment: 0.3,
    monitoring: 0.25,
    strategy: 0.2,
};

const questions: Question[] = [
    {
        id: 'vendorCount',
        step: 'inventory',
        prompt: 'How many active vendors does your organization currently manage?',
        type: 'single',
        options: [
            { value: '1-10', label: '1-10 vendors', helper: 'Light vendor footprint', score: 4.5 },
            { value: '11-25', label: '11-25 vendors', helper: 'Growing vendor landscape', score: 3.5 },
            { value: '26-100', label: '26-100 vendors', helper: 'Established vendor program', score: 2.5 },
            { value: '100+', label: '100+ vendors', helper: 'Enterprise-scale ecosystem', score: 1.5 },
        ],
    },
    {
        id: 'dataSensitivity',
        step: 'inventory',
        prompt: 'What is the most sensitive data your vendors can access or process?',
        type: 'single',
        options: [
            { value: 'basic', label: 'Basic corporate / internal only', score: 4.5 },
            { value: 'pii', label: 'PII / customer-sensitive information', score: 3 },
            { value: 'phi', label: 'PHI / PCI or regulated financial data', score: 1.5 },
        ],
    },
    {
        id: 'assessmentMethod',
        step: 'assessment',
        prompt: 'How do you assess new vendors before granting access?',
        type: 'single',
        options: [
            { value: 'none', label: 'We do not currently assess new vendors', score: 0.5 },
            { value: 'email', label: 'Ad-hoc questionnaires via email or spreadsheets', score: 2.5 },
            { value: 'tool', label: 'Dedicated vendor risk platform or portal', score: 4 },
            { value: 'managed', label: 'Managed service partner handles it for us', score: 4.5 },
        ],
    },
    {
        id: 'evidenceReview',
        step: 'assessment',
        prompt: 'Do you validate security evidence (SOC 2, pen test, etc.) during onboarding?',
        type: 'single',
        options: [
            { value: 'no', label: 'No, we grant access without reviewing evidence', score: 0.5 },
            { value: 'sometimes', label: 'Sometimes or rely on vendor self-attestation', score: 2.5 },
            { value: 'yes', label: 'Yes, we always require and review formal evidence', score: 4.5 },
        ],
    },
    {
        id: 'proactiveAlerts',
        step: 'monitoring',
        prompt: 'How do you detect vendor issues like breaches or certificate expirations?',
        type: 'single',
        options: [
            { value: 'none', label: 'We typically learn from the news or vendor outreach', score: 0.5 },
            { value: 'manual', label: 'We use manual tracking or periodic checks', score: 2.5 },
            { value: 'automated', label: 'We receive automated alerts or managed notifications', score: 4.5 },
        ],
    },
    {
        id: 'incidentPlaybook',
        step: 'monitoring',
        prompt: 'Do you have a documented incident response playbook with vendor-specific actions?',
        type: 'single',
        options: [
            { value: 'no', label: 'No, we would improvise if a vendor incident occurs', score: 1 },
            { value: 'partial', label: 'Partially documented or informal guidance', score: 2.5 },
            { value: 'yes', label: 'Yes, we have a documented and tested playbook', score: 4.5 },
        ],
    },
    {
        id: 'complianceFocus',
        step: 'strategy',
        prompt: 'Which regulatory or industry frameworks are top of mind for your VRM program?',
        type: 'multi',
        options: [
            { value: 'soc2', label: 'SOC 2', score: 1.5 },
            { value: 'hipaa', label: 'HIPAA', score: 1.5 },
            { value: 'pcidss', label: 'PCI-DSS', score: 1.5 },
            { value: 'nist', label: 'NIST CSF/800-171', score: 1.5 },
            { value: 'none', label: 'None / not sure', score: 0 },
        ],
    },
    {
        id: 'implementationTimeline',
        step: 'strategy',
        prompt: 'What is your timeline for standing up or formalizing a VRM program?',
        type: 'single',
        options: [
            { value: '0-3', label: '0-3 months', score: 4.5 },
            { value: '3-6', label: '3-6 months', score: 4 },
            { value: '6+', label: '6+ months', score: 2.5 },
            { value: 'undecided', label: 'Undecided or gathering information', score: 1.5 },
        ],
    },
];

const sleMatrix: Record<string, Record<string, number>> = {
    phi: {
        '1-10': 3000000,
        '11-25': 4000000,
        '26-100': 5000000,
        '100+': 6000000,
    },
    pii: {
        '1-10': 1000000,
        '11-25': 1500000,
        '26-100': 2500000,
        '100+': 3500000,
    },
    basic: {
        '1-10': 500000,
        '11-25': 900000,
        '26-100': 1500000,
        '100+': 2200000,
    },
};

const maturityBands = [
    { min: 0, max: 1.5, label: 'Basic', insight: 'Foundational controls are missing, leaving you exposed to high vendor breach likelihood.' },
    { min: 1.6, max: 3, label: 'Developing', insight: 'Core assessment processes exist but lack consistency and automation.' },
    { min: 3.1, max: 4.5, label: 'Audit-Ready', insight: 'Strong controls in place, but visibility and resilience can still improve.' },
    { min: 4.6, max: 5, label: 'Breach-Proof', insight: 'World-class VRM maturity with proactive governance and resilience.' },
];

const stepsMeta: Record<StepId, { title: string; description: string }> = {
    inventory: {
        title: 'Inventory & Scope',
        description: 'Define the scope of your vendor landscape and risk surface.',
    },
    assessment: {
        title: 'Assessment & Approval',
        description: 'Understand how you qualify the vendors you rely on.',
    },
    monitoring: {
        title: 'Monitoring & Response',
        description: 'Gauge your ability to sense and respond to third-party risk.',
    },
    strategy: {
        title: 'Strategy & Maturity',
        description: 'Highlight executive alignment and transformation urgency.',
    },
};

const totalQuestions = questions.length;
const AUTO_ADVANCE_DELAY = 220;
const PLANS_URL = 'https://inventivehq.com/services/vendor-risk-management#pricing';

const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    // USD's default minimumFractionDigits is 2; without pinning it to 0 here,
    // older Intl engines reject maximumFractionDigits:0 with
    // "maximumFractionDigits value is out of range".
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});

function computePillarScores(answers: Answers): PillarResults {
    const initial: PillarResults = {
        inventory: 0,
        assessment: 0,
        monitoring: 0,
        strategy: 0,
    };

    const counts: Record<StepId, number> = {
        inventory: 0,
        assessment: 0,
        monitoring: 0,
        strategy: 0,
    };

    questions.forEach((question) => {
        const value = answers[question.id];
        if (!value || (Array.isArray(value) && value.length === 0)) {
            return;
        }

        if (question.type === 'single') {
            const option = question.options.find((opt) => opt.value === value);
            if (option) {
                initial[question.step] += option.score;
                counts[question.step] += 1;
            }
        } else {
            const selections = Array.isArray(value) ? value : [];
            const uniqueSelections = selections.includes('none') ? ['none'] : selections;
            if (uniqueSelections.length === 0) {
                return;
            }
            const totalScore = uniqueSelections.reduce((sum, choice) => {
                const option = question.options.find((opt) => opt.value === choice);
                return sum + (option?.score ?? 0);
            }, 0);
            const average = totalScore / uniqueSelections.length;
            initial[question.step] += average;
            counts[question.step] += 1;
        }
    });

    (Object.keys(initial) as StepId[]).forEach((pillar) => {
        if (counts[pillar] > 0) {
            initial[pillar] = Number((initial[pillar] / counts[pillar]).toFixed(2));
        }
    });

    return initial;
}

function computeMaturityScore(pillarScores: PillarResults): number {
    const score = (pillarScores.inventory * weights.inventory) +
        (pillarScores.assessment * weights.assessment) +
        (pillarScores.monitoring * weights.monitoring) +
        (pillarScores.strategy * weights.strategy);
    return Number((score).toFixed(2));
}

function determineMaturityTier(score: number) {
    const band = maturityBands.find((tier) => score >= tier.min && score <= tier.max);
    return band ?? maturityBands[0];
}

function computeSLE(sensitivity?: string, vendorCount?: string) {
    if (!sensitivity || !vendorCount) {
        return 0;
    }
    const matrix = sleMatrix[sensitivity];
    if (!matrix) {
        return 0;
    }
    return matrix[vendorCount] ?? 0;
}

function computeARO(score: number) {
    if (score <= 1.5) {
        return 0.4;
    }
    if (score <= 3) {
        return 0.28;
    }
    if (score <= 4.5) {
        return 0.18;
    }
    return 0.1;
}

function formatPillar(pillar: StepId) {
    switch (pillar) {
        case 'inventory':
            return 'Inventory & Scope';
        case 'assessment':
            return 'Assessment & Approval';
        case 'monitoring':
            return 'Monitoring & Response';
        case 'strategy':
            return 'Strategy & Maturity';
        default:
            return pillar;
    }
}

/* eslint-disable @next/next/no-html-link-for-pages -- R2 bundles cannot use next/link */
function renderFoundationRecommendation(vendorCount?: string, tierLabel?: string) {
    if (!vendorCount) {
        return <>Complete the scorecard to receive your <a href={PLANS_URL} className="text-[#2813e8] underline underline-offset-2">Foundation Program</a> roadmap and onboarding plan.</>;
    }
    return <>Based on your {vendorCount} vendors and {tierLabel ?? 'current'} maturity, we recommend the <a href={PLANS_URL} className="text-[#2813e8] underline underline-offset-2">Foundation Program</a> with expert-led onboarding.</>;
}
/* eslint-enable @next/next/no-html-link-for-pages */

const requiredFields: (keyof Answers)[] = [
    'vendorCount',
    'dataSensitivity',
    'assessmentMethod',
    'evidenceReview',
    'proactiveAlerts',
    'incidentPlaybook',
    'complianceFocus',
    'implementationTimeline',
];

export type VRMBreachProofScorecardProps = {
    className?: string;
};

export function VRMBreachProofScorecard({ className }: VRMBreachProofScorecardProps) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [answers, setAnswers] = useState<Answers>({
        complianceFocus: [],
    });

    const autoAdvanceTimeout = useRef<number | null>(null);

    useEffect(() => {
        return () => {
            if (autoAdvanceTimeout.current) {
                window.clearTimeout(autoAdvanceTimeout.current);
            }
        };
    }, []);

    const pillarScores = useMemo(() => computePillarScores(answers), [answers]);
    const maturityScore = useMemo(() => computeMaturityScore(pillarScores), [pillarScores]);
    const maturityTier = useMemo(() => determineMaturityTier(maturityScore), [maturityScore]);
    const sle = useMemo(() => computeSLE(answers.dataSensitivity, answers.vendorCount), [answers.dataSensitivity, answers.vendorCount]);
    const aro = useMemo(() => computeARO(maturityScore), [maturityScore]);
    const ale = useMemo(() => Math.round(sle * aro), [sle, aro]);

    const lowestPillar = useMemo(() => {
        const entries = (Object.entries(pillarScores) as [StepId, number][]).filter(([, value]) => value > 0);
        if (entries.length === 0) {
            return null;
        }
        return entries.reduce((acc, item) => (item[1] < acc[1] ? item : acc))[0];
    }, [pillarScores]);

    const isComplete = currentIndex >= totalQuestions;
    const progress = isComplete ? 1 : (currentIndex + 1) / totalQuestions;
    const currentQuestion = isComplete ? null : questions[currentIndex];
    const questionNumber = currentIndex + 1;

    const allPillarsCompleted = requiredFields.every((key) => {
        const value = answers[key];
        if (Array.isArray(value)) {
            return value.length > 0;
        }
        return Boolean(value);
    });

    const scheduleAutoAdvance = () => {
        if (autoAdvanceTimeout.current) {
            window.clearTimeout(autoAdvanceTimeout.current);
        }
        autoAdvanceTimeout.current = window.setTimeout(() => {
            setCurrentIndex((prev) => Math.min(prev + 1, totalQuestions));
        }, AUTO_ADVANCE_DELAY);
    };

    const handleSingleSelect = (questionId: keyof Answers, value: string) => {
        setAnswers((prev) => ({
            ...prev,
            [questionId]: value,
        }));
        scheduleAutoAdvance();
    };

    const handleMultiSelect = (questionId: keyof Answers, value: string) => {
        let shouldAutoAdvance = false;
        setAnswers((prev) => {
            const currentValues = Array.isArray(prev[questionId]) ? [...prev[questionId] as string[]] : [];
            let nextValues: string[];

            if (value === 'none') {
                if (currentValues.includes('none')) {
                    nextValues = [];
                } else {
                    nextValues = ['none'];
                    shouldAutoAdvance = true;
                }
            } else {
                const filtered = currentValues.filter((item) => item !== 'none');
                if (filtered.includes(value)) {
                    nextValues = filtered.filter((item) => item !== value);
                } else {
                    nextValues = [...filtered, value];
                }
            }

            return {
                ...prev,
                [questionId]: nextValues,
            };
        });

        if (value === 'none' && shouldAutoAdvance) {
            scheduleAutoAdvance();
        } else if (value !== 'none') {
            if (autoAdvanceTimeout.current) {
                window.clearTimeout(autoAdvanceTimeout.current);
                autoAdvanceTimeout.current = null;
            }
        }
    };

    const handleBack = () => {
        if (autoAdvanceTimeout.current) {
            window.clearTimeout(autoAdvanceTimeout.current);
            autoAdvanceTimeout.current = null;
        }
        setCurrentIndex((prev) => Math.max(prev - 1, 0));
    };

    const handleAdvance = () => {
        if (autoAdvanceTimeout.current) {
            window.clearTimeout(autoAdvanceTimeout.current);
            autoAdvanceTimeout.current = null;
        }
        setCurrentIndex((prev) => Math.min(prev + 1, totalQuestions));
    };

    const handleRestart = () => {
        if (autoAdvanceTimeout.current) {
            window.clearTimeout(autoAdvanceTimeout.current);
            autoAdvanceTimeout.current = null;
        }
        setAnswers({ complianceFocus: [] });
        setCurrentIndex(0);
    };

    if (isComplete) {
        return (<div className={clsx('bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 md:p-10', className)}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Your VRM "Breach-Proof" Scorecard</h2>
            <p className="text-gray-600 dark:text-gray-400">Review your maturity score, top risks, and financial exposure.</p>
          </div>
          <div className="w-full md:w-64">
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#2813e8] to-[#2cc3d1]" style={{ width: `${Math.round(progress * 100)}%` }}/>
            </div>
            <p className="text-sm text-right text-gray-500 dark:text-gray-400 mt-1">Scorecard Complete</p>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2 mb-10">
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6">
            <p className="text-sm uppercase font-semibold text-gray-500 dark:text-gray-400 tracking-wide">VRM Maturity Score</p>
            <p className="text-4xl font-bold text-gray-900 dark:text-gray-100 mt-2">{maturityScore.toFixed(1)} / 5.0</p>
            <p className="text-lg font-semibold text-[#2813e8] mt-1">{maturityTier.label}</p>
            <p className="text-gray-600 dark:text-gray-400 mt-4">{maturityTier.insight}</p>
          </div>
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6">
            <p className="text-sm uppercase font-semibold text-gray-500 dark:text-gray-400 tracking-wide">Estimated Annual Risk Exposure (ALE)</p>
            <p className="text-4xl font-bold text-gray-900 dark:text-gray-100 mt-2">{ale ? currencyFormatter.format(ale) : '—'}</p>
            <div className="mt-4 space-y-1 text-sm text-gray-600 dark:text-gray-400">
              <p>SLE (Single Loss Expectancy): {sle ? currencyFormatter.format(sle) : '—'}</p>
              <p>ARO (Annual Rate of Occurrence): {(aro * 100).toFixed(0)}%</p>
            </div>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2 mb-10">
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">Pillar Performance</h3>
            <ul className="space-y-3">
              {(Object.entries(pillarScores) as [StepId, number][]).map(([pillar, score]) => (
                        <li key={pillar} className="flex items-center justify-between">
                          <span className="text-gray-700 dark:text-gray-300">{formatPillar(pillar)}</span>
                          <span className="font-semibold text-gray-900 dark:text-gray-100">{score ? score.toFixed(1) : '—'}</span>
                        </li>))}
            </ul>
          </div>
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">Immediate Risk Insight</h3>
            <p className="text-gray-600 dark:text-gray-400">
              {lowestPillar ? `Your biggest opportunity is in ${formatPillar(lowestPillar)} — strengthening this pillar will lower breach likelihood the fastest.` : 'Complete the scorecard to reveal your highest-risk pillar.'}
            </p>
            <div className="mt-4">
              <p className="text-sm uppercase font-semibold text-gray-500 dark:text-gray-400 tracking-wide">Recommended Next Step</p>
              <p className="text-gray-700 dark:text-gray-300 mt-1">{renderFoundationRecommendation(answers.vendorCount, maturityTier.label)}</p>
            </div>
          </div>
        </div>

        {/* Lead Capture Form */}
        <LeadCaptureForm
          source="vrm-breach-proof-scorecard"
          title="Get Expert Vendor Risk Guidance"
          description="Our vendor risk management experts will review your scorecard results and help you build a breach-proof VRM program."
          deliverable="a customized vendor risk management roadmap"
          resultsSummary={(() => {
            const pillarSummary = (Object.entries(pillarScores) as [StepId, number][])
              .map(([pillar, score]) => `• ${formatPillar(pillar)}: ${score ? score.toFixed(1) : '—'}/5.0`)
              .join('\n');

            return `VRM BREACH-PROOF SCORECARD RESULTS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
VRM Maturity Score: ${maturityScore.toFixed(1)} / 5.0
Maturity Tier: ${maturityTier.label}
${maturityTier.insight}

ESTIMATED ANNUAL RISK EXPOSURE (ALE): ${ale ? currencyFormatter.format(ale) : '—'}
• Single Loss Expectancy (SLE): ${sle ? currencyFormatter.format(sle) : '—'}
• Annual Rate of Occurrence (ARO): ${(aro * 100).toFixed(0)}%

PILLAR PERFORMANCE:
${pillarSummary}

BIGGEST OPPORTUNITY: ${lowestPillar ? formatPillar(lowestPillar) : 'Complete assessment'}

VENDOR PROFILE:
• Active Vendors: ${answers.vendorCount || 'Not specified'}
• Data Sensitivity: ${answers.dataSensitivity || 'Not specified'}
• Assessment Method: ${answers.assessmentMethod || 'Not specified'}
• Implementation Timeline: ${answers.implementationTimeline || 'Not specified'}`;
          })()}
        />

        <div className="flex items-center justify-between mt-8">
          <button className="text-sm text-[#2813e8] hover:text-[#1d0db0]" onClick={() => setCurrentIndex(Math.max(totalQuestions - 1, 0))}>
            ← Edit Previous Answers
          </button>
          <button className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-gray-900 text-white font-semibold hover:bg-black/80 transition-colors" onClick={handleRestart}>
            Start Over
          </button>
        </div>
      </div>);
    }

    if (!currentQuestion) {
        return null;
    }

    const pillarMeta = stepsMeta[currentQuestion.step];
    const selectedValue = typeof answers[currentQuestion.id] === 'string' ? answers[currentQuestion.id] : undefined;
    const selections = currentQuestion.type === 'multi' && Array.isArray(answers[currentQuestion.id]) ? answers[currentQuestion.id] as string[] : [];
    const canContinue = currentQuestion.type === 'multi' ? selections.length > 0 : true;

    return (<div className={clsx('bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 md:p-10', className)}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <p className="text-sm font-semibold text-[#2813e8] uppercase tracking-wide">Question {questionNumber} of {totalQuestions}</p>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{pillarMeta.title}</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">{pillarMeta.description}</p>
        </div>
        <div className="w-full md:w-64">
          <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#2813e8] to-[#2cc3d1]" style={{ width: `${Math.round(progress * 100)}%` }}/>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{currentQuestion.prompt}</h3>
        {currentQuestion.type === 'single' ? (<div className="grid gap-4 mt-6 md:grid-cols-2">
            {currentQuestion.options.map((option) => {
                    const isActive = selectedValue === option.value;
                    return (<button key={option.value} type="button" onClick={() => handleSingleSelect(currentQuestion.id, option.value)} className={clsx('border rounded-xl px-4 py-4 text-left transition-all', isActive ? 'border-[#2813e8] bg-[#2813e8]/5 shadow-sm' : 'border-gray-200 dark:border-gray-700 hover:border-[#2813e8]/60')}>
                    <span className="block text-lg font-semibold text-gray-900 dark:text-gray-100">{option.label}</span>
                    {option.helper ? (<span className="block text-sm text-gray-500 dark:text-gray-400 mt-1">{option.helper}</span>) : null}
                  </button>);
                })}
          </div>) : (<div className="mt-6">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Select all that apply, then continue.</p>
            <div className="grid gap-3 md:grid-cols-2">
              {currentQuestion.options.map((option) => {
                        const isActive = selections.includes(option.value);
                        return (<button key={option.value} type="button" onClick={() => handleMultiSelect(currentQuestion.id, option.value)} className={clsx('border rounded-xl px-4 py-4 text-left transition-all', isActive ? 'border-[#2813e8] bg-[#2813e8]/5 shadow-sm' : 'border-gray-200 dark:border-gray-700 hover:border-[#2813e8]/60')}>
                        <span className="block text-lg font-semibold text-gray-900 dark:text-gray-100">{option.label}</span>
                      </button>);
                    })}
            </div>
          </div>)}
      </div>

      <div className="flex items-center justify-between mt-10">
        <button className="text-sm text-[#2813e8] hover:text-[#1d0db0] disabled:text-gray-400 disabled:hover:text-gray-400" onClick={handleBack} disabled={currentIndex === 0}>
          ← Previous
        </button>
        {currentQuestion.type === 'multi' ? (<button className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-[#2813e8] text-white font-semibold hover:bg-[#1d0db0] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed" onClick={handleAdvance} disabled={!canContinue}>
            Continue
          </button>) : (<span className="text-sm text-gray-400">Select an option to continue</span>)}
      </div>

        {!allPillarsCompleted ? (<p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
          Answer every question to unlock your personalized maturity score and ALE exposure calculation.
        </p>) : null}
    </div>);
}
